import { PieChart } from '@mui/x-charts';
import { Skeleton } from '@mui/material';

import { Text } from '@fragments';

import {
    formatMoneyFull,
    formatNumberCompact,
    formatCountFull,
} from '../helpers';

import '../styles.scss';
import '../TimeSeriesChart/styles.scss';
import './styles.scss';

const PIE_SIZE = 220;

// A donut card matching the TimeSeriesChart chrome (`ts-chart-card`). Renders a
// small set of slices with a center total and a house-styled legend beside it.
//
// Each slice: { id, label, value, color } — `color` must already be a resolved
// hex string (MUI x-charts hands series colors to d3-color, which cannot parse
// `var(...)`), so callers resolve CSS vars with useResolvedCssColor first.
export default function DonutChart({
    title,
    icon,
    slices = [],
    valueType = 'number', // 'number' | 'money'
    currency = 'BRL',
    unitSingular = 'item',
    unitPlural = 'itens',
    centerLabel,
    emptyMessage = 'Sem dados para o período selecionado.',
    report,
    reportLoading,
}) {
    const isMoney = valueType === 'money';

    const total = slices.reduce((acc, s) => acc + (Number(s.value) || 0), 0);

    const formatFull = (value) =>
        isMoney ? formatMoneyFull(value, currency) : formatCountFull(value, unitSingular, unitPlural);

    const centerValue = isMoney ? formatMoneyFull(total, currency) : formatNumberCompact(total);

    const pieData = slices.map((s) => ({
        id: s.id,
        value: Number(s.value) || 0,
        label: s.label,
        color: s.color,
    }));

    return (
        <div className={`ts-chart-card donut-chart${reportLoading && report ? ' ts-chart-card--reloading' : ''}`}>
            <div className="ts-chart-card__header">
                <div className="ts-chart-card__title">
                    <span className="ts-chart-card__icon">{icon}</span>
                    <Text.h4 contrast={7}>{title}</Text.h4>
                    <Text.span contrast={4} className="ts-chart-card__count">
                        · {formatFull(total)} no total
                    </Text.span>
                </div>
            </div>

            <div className="ts-chart-card__body donut-chart__body">
                {reportLoading && !report ? (
                    <Skeleton variant="rounded" height={260} className="ts-chart-card__skeleton" />
                ) : total <= 0 ? (
                    <div className="ts-chart-card__empty">
                        <Text.p contrast={5}>{emptyMessage}</Text.p>
                    </div>
                ) : (
                    <div className="donut-chart__content">
                        <div className="donut-chart__pie" style={{ width: PIE_SIZE, height: PIE_SIZE }}>
                            <PieChart
                                width={PIE_SIZE}
                                height={PIE_SIZE}
                                margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
                                series={[{
                                    data: pieData,
                                    innerRadius: PIE_SIZE * 0.30,
                                    outerRadius: PIE_SIZE * 0.46,
                                    paddingAngle: 2,
                                    cornerRadius: 4,
                                    highlightScope: { fade: 'global', highlight: 'item' },
                                    valueFormatter: (item) => formatFull(item.value),
                                }]}
                                slotProps={{ legend: { hidden: true } }}
                                sx={{
                                    // paddingAngle already separates the arcs; drop the theme-colored
                                    // gap stroke so the seam shows the card surface, not MUI's paper color.
                                    '.MuiPieArc-root': { strokeWidth: 0 },
                                }}
                            />
                            <div className="donut-chart__center">
                                <Text.h4 contrast={9} weight={500}>{centerValue}</Text.h4>
                                {centerLabel && <Text.caption contrast={5}>{centerLabel}</Text.caption>}
                            </div>
                        </div>

                        <ul className="donut-chart__legend">
                            {slices.map((s) => {
                                const value = Number(s.value) || 0;
                                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                                return (
                                    <li className="donut-chart__legend-row" key={s.id}>
                                        <span className="donut-chart__swatch" style={{ backgroundColor: s.color }} />
                                        <Text.label contrast={9} grow>{s.label}</Text.label>
                                        <span className="donut-chart__legend-value">
                                            <Text.span contrast={7}>{formatFull(value)}</Text.span>
                                            <Text.caption contrast={5}>{pct}%</Text.caption>
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
